import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { ChatToggleButton } from "@/components/AIChatWidget";
import { FlowguardLogo } from "@/components/FlowguardLogo";
import {
  Building2, Home, TrendingUp, Calculator, BarChart3,
  Plus, LogOut, Settings, ChevronRight, DollarSign,
  FileText, Trash2, Crown, Sparkles, RefreshCw, Briefcase, GitCompare, Globe, Search, LineChart, Users, Landmark, Hammer,
  Wallet, PiggyBank, Target, Award, TrendingDown, ArrowUpRight, ArrowDownRight, Percent, Zap, MessageSquare, Shield
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CountrySelector from "@/components/CountrySelector";
import FeedbackModal from "@/components/FeedbackModal";
import { publicSitePath } from "@/utils/publicSite";

const PENDING_UPGRADE_KEY = "flowguard_pending_upgrade";
const PENDING_UPGRADE_MAX_AGE_MS = 30 * 60 * 1000;
const PLAN_SYNC_TOAST_ID = "flowguard-plan-sync";

const normalizeTier = (tier) => {
  const normalized = String(tier || "free").toLowerCase();
  return normalized === "light" ? "free" : normalized;
};

const tierRank = (tier) => {
  const ranks = { free: 0, starter: 1, pro: 2, developer: 3 };
  return ranks[normalizeTier(tier)] ?? 0;
};

const readPendingUpgrade = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_UPGRADE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const plan = normalizeTier(parsed?.plan);
    const createdAt = Number(parsed?.createdAt || 0);
    const isFresh = createdAt && Date.now() - createdAt <= PENDING_UPGRADE_MAX_AGE_MS;

    if (!["starter", "pro"].includes(plan) || !isFresh) {
      sessionStorage.removeItem(PENDING_UPGRADE_KEY);
      return null;
    }

    return { plan, createdAt };
  } catch (_error) {
    sessionStorage.removeItem(PENDING_UPGRADE_KEY);
    return null;
  }
};

const Dashboard = () => {
  const { user, token, logout, updateTier, updatePreferences, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const planSyncToastShownRef = useRef(false);

  useEffect(() => {
    if (token) {
      fetchStats();
      checkAdminStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;

    const pendingUpgrade = readPendingUpgrade();
    if (!pendingUpgrade) return;

    let cancelled = false;
    let timeoutId = null;
    let attempts = 0;
    const maxAttempts = 20;
    const intervalMs = 3000;

    const syncUpgrade = async () => {
      if (cancelled) return;
      attempts += 1;

      try {
        const refreshed = await refreshUser();
        if (tierRank(refreshed?.tier) >= tierRank(pendingUpgrade.plan)) {
          sessionStorage.removeItem(PENDING_UPGRADE_KEY);
          toast.dismiss(PLAN_SYNC_TOAST_ID);
          toast.success(
            pendingUpgrade.plan === "pro"
              ? "Welcome to Pro Investor!"
              : "Welcome to Starter Investor!"
          );
          fetchStats();
          checkAdminStatus();
          return;
        }
      } catch (_error) {
        // Keep the dashboard usable; the global auth interceptor handles true auth failures.
      }

      if (attempts < maxAttempts) {
        timeoutId = setTimeout(syncUpgrade, intervalMs);
      } else {
        sessionStorage.removeItem(PENDING_UPGRADE_KEY);
        toast.dismiss(PLAN_SYNC_TOAST_ID);
        toast.info("Your payment is still being finalized. Refresh the dashboard in a moment if your plan has not updated.");
      }
    };

    if (!planSyncToastShownRef.current) {
      planSyncToastShownRef.current = true;
      toast.info("Syncing your new plan...", { id: PLAN_SYNC_TOAST_ID });
    }
    syncUpgrade();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // This runs only after returning from checkout with a locally stored pending upgrade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = publicSitePath("/");
  };

  const handleUpgradeTier = async (tier) => {
    try {
      await updateTier(tier);
      toast.success(`Upgraded to ${tier === 'pro' ? 'Pro Investor' : 'Developer'} tier!`);
    } catch (error) {
      toast.error("Failed to upgrade tier");
    }
  };

  const handleCountryChange = async (countryCode) => {
    try {
      await updatePreferences({ default_country: countryCode });
      toast.success(`Default country changed to ${countryCode === 'CA' ? 'Canada' : 'United States'}`);
    } catch (error) {
      toast.error("Failed to update country");
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const tools = [
    {
      name: "Quick Screen",
      desc: "5-minute deal screening with instant Go/No-Go",
      icon: Zap,
      path: "/quick-screen",
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      tier: "free",
      featured: true
    },
    {
      name: "BRRRR Calculator",
      desc: "Buy, Rehab, Rent, Refinance, Repeat",
      icon: RefreshCw,
      path: "/brrrr-calculator",
      color: "text-pink-500",
      bgColor: "bg-pink-500/10",
      tier: "free"
    },
    {
      name: "Deal Manager",
      desc: "Create & track deals with scoring",
      icon: FileText,
      path: "/deals",
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      tier: "free",
      lightLimit: "3 properties max"
    },
    {
      name: "Report Vault",
      desc: "Download investment reports",
      icon: FileText,
      path: "/reports",
      color: "text-violet-500",
      bgColor: "bg-violet-500/10",
      tier: "free",
      lightLimit: "Upgrade to generate reports"
    },
    {
      name: "House Flipping",
      desc: "Calculate flip profits",
      icon: TrendingUp,
      path: "/flip-calculator",
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      tier: "free"
    },
    {
      name: "Rent Estimator",
      desc: "Estimate market rent",
      icon: DollarSign,
      path: "/rent-estimator",
      color: "text-violet-500",
      bgColor: "bg-violet-500/10",
      tier: "free",
      lightLimit: "Single-family only"
    },
    {
      name: "Mortgage Calculator",
      desc: "Loan & amortization",
      icon: Calculator,
      path: "/mortgage-calculator",
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      tier: "free"
    },
    {
      name: "Rental Analysis",
      desc: "Analyze buy-and-hold properties",
      icon: Home,
      path: "/rental-analysis",
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10",
      tier: "free",
      lightLimit: "Single-family only"
    },
    {
      name: "Tax Strategy",
      desc: "Depreciation, 1031 exchange, cost seg",
      icon: Landmark,
      path: "/tax-strategy",
      color: "text-rose-500",
      bgColor: "bg-rose-500/10",
      tier: "pro"
    },
    {
      name: "Renovation Estimator",
      desc: "Estimate rehab costs & BRRRR scenarios",
      icon: Hammer,
      path: "/renovation-estimator",
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
      tier: "pro"
    },
    {
      name: "Property Research",
      desc: "Market data, comps & rent estimates",
      icon: Search,
      path: "/property-research",
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      tier: "pro",
      isNew: true
    },
    {
      name: "Airbnb Calculator",
      desc: "Short-term rental analysis",
      icon: BarChart3,
      path: "/airbnb-calculator",
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      tier: "pro"
    },
    {
      name: "Portfolio Tracker",
      desc: "Track all your properties",
      icon: Briefcase,
      path: "/portfolio",
      color: "text-indigo-500",
      bgColor: "bg-indigo-500/10",
      tier: "pro"
    },
    {
      name: "Deal Comparison",
      desc: "Compare deals side-by-side",
      icon: GitCompare,
      path: "/compare",
      color: "text-teal-500",
      bgColor: "bg-teal-500/10",
      tier: "pro"
    },
    {
      name: "Expense Templates",
      desc: "Save expense profiles",
      icon: FileText,
      path: "/expense-templates",
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
      tier: "pro"
    },
    {
      name: "Partnership Mode",
      desc: "Multi-investor deal structures",
      icon: Users,
      path: "/partnerships",
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      tier: "pro"
    },
  ];

  const analysisTypeMeta = {
    brrrr: { label: 'BRRRR', icon: RefreshCw, pathPrefix: '/brrrr-calculator' },
    rental: { label: 'Rental', icon: Home, pathPrefix: '/rental-analysis' },
    flip: { label: 'Flip', icon: TrendingUp, pathPrefix: '/flip-calculator' },
    airbnb: { label: 'Airbnb', icon: BarChart3, pathPrefix: '/airbnb-calculator' },
    deal: { label: 'Deal', icon: FileText, pathPrefix: '/deals' }
  };

  const normalizeTier = (tier) => tier === 'light' ? 'free' : (tier || 'free');
  const normalizedUserTier = normalizeTier(user?.tier);
  const tierLevels = { free: 1, light: 1, starter: 2, pro: 3, developer: 4 };
  const userTierLevel = tierLevels[normalizedUserTier] || 1;

  const getPlanLabel = (tier) => {
    const normalizedTier = normalizeTier(tier);
    if (normalizedTier === 'developer') return 'Developer (legacy)';
    if (normalizedTier === 'pro') return 'Pro Investor';
    if (normalizedTier === 'starter') return 'Starter Investor';
    return 'Free Investor';
  };

  const getTierBadgeStyles = (tier) => {
    const normalizedTier = normalizeTier(tier);
    if (normalizedTier === "starter") {
      return {
        icon: "text-emerald-400",
        container: "bg-emerald-500/10 border-emerald-500/40"
      };
    }
    if (normalizedTier === "pro") {
      return {
        icon: "text-amber-500",
        container: "bg-amber-500/10 border-amber-500/40"
      };
    }
    if (normalizedTier === "developer") {
      return {
        icon: "text-amber-500",
        container: "bg-amber-500/10 border-amber-500/30"
      };
    }
    return {
      icon: "text-slate-400",
      container: "bg-slate-800 border-slate-700"
    };
  };

  const tierBadge = getTierBadgeStyles(normalizedUserTier);

  const isToolAccessible = (toolTier) => {
    return tierLevels[toolTier] <= userTierLevel;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="dashboard-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard">
              <FlowguardLogo size="md" />
            </Link>

            <div className="flex items-center gap-3">
              {/* Admin Dashboard Link (only for admins) */}
              {isAdmin && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin')}
                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                  data-testid="admin-dashboard-link"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              )}

              {/* Send Feedback Button */}
              <Button
                variant="ghost"
                onClick={() => setFeedbackOpen(true)}
                className="text-slate-400 hover:text-white"
                data-testid="send-feedback-btn"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Feedback</span>
              </Button>

              {/* Country Selector */}
              <CountrySelector
                value={user?.preferences?.default_country || 'CA'}
                onChange={handleCountryChange}
                compact={true}
              />

              <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border ${tierBadge.container}`}>
                <Crown className={`h-4 w-4 ${tierBadge.icon}`} />
                <span className="text-sm text-slate-300">
                  {getPlanLabel(user?.tier)}
                </span>
              </div>

              <ChatToggleButton />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-slate-300 hover:text-white">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                      <span className="text-sm font-bold text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
                  <DropdownMenuItem
                    className="text-slate-400 focus:text-white focus:bg-slate-800 cursor-pointer"
                    onClick={handleSettingsClick}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  {normalizedUserTier !== 'pro' && normalizedUserTier !== 'developer' && (
                    <>
                      <DropdownMenuItem
                        className="text-amber-500 focus:text-amber-400 focus:bg-slate-800 cursor-pointer"
                        onClick={() => navigate('/upgrade')}
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        Upgrade to {normalizedUserTier === 'free' ? 'Starter or Pro' : 'Pro'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-slate-800" />
                    </>
                  )}
                  <DropdownMenuItem
                    className="text-red-500 focus:text-red-400 focus:bg-slate-800 cursor-pointer"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Manrope'] mb-2">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-400">Ready to analyze your next investment?</p>
        </div>

        {/* Portfolio Analytics Section */}
        {(stats?.portfolio?.total_properties > 0 || stats?.deals?.total_deals > 0) && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white font-['Manrope'] flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-amber-500" />
                Portfolio Overview
              </h2>
              <Button
                variant="ghost"
                className="text-amber-500 hover:text-amber-400 text-sm"
                onClick={() => navigate("/portfolio")}
              >
                View Details
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* Main Portfolio Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Total Portfolio Value */}
              <div className="glass-card rounded-xl p-5 border border-slate-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10">
                    <Wallet className="h-5 w-5 text-emerald-500" />
                  </div>
                  {stats?.portfolio?.total_value > 0 && (
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      <ArrowUpRight className="h-3 w-3" />
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400 mb-1">Total Portfolio Value</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(stats?.portfolio?.total_value || 0)}</p>
                <p className="text-xs text-slate-500 mt-1">{stats?.portfolio?.total_properties || 0} owned properties</p>
              </div>

              {/* Total Equity */}
              <div className="glass-card rounded-xl p-5 border border-slate-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10">
                    <PiggyBank className="h-5 w-5 text-cyan-500" />
                  </div>
                  {stats?.portfolio?.ltv_ratio > 0 && (
                    <span className="text-xs text-slate-400">
                      {stats?.portfolio?.ltv_ratio}% LTV
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400 mb-1">Total Equity</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(stats?.portfolio?.total_equity || 0)}</p>
                <p className="text-xs text-slate-500 mt-1">Debt: {formatCurrency(stats?.portfolio?.total_debt || 0)}</p>
              </div>

              {/* Monthly Cash Flow */}
              <div className="glass-card rounded-xl p-5 border border-slate-800">
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 rounded-lg ${(stats?.portfolio?.monthly_cash_flow || 0) >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                    <DollarSign className={`h-5 w-5 ${(stats?.portfolio?.monthly_cash_flow || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
                  </div>
                  {(stats?.portfolio?.monthly_cash_flow || 0) !== 0 && (
                    <span className={`text-xs flex items-center gap-1 ${(stats?.portfolio?.monthly_cash_flow || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(stats?.portfolio?.monthly_cash_flow || 0) >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {(stats?.portfolio?.monthly_cash_flow || 0) >= 0 ? 'Positive' : 'Negative'}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400 mb-1">Monthly Cash Flow</p>
                <p className={`text-2xl font-bold ${(stats?.portfolio?.monthly_cash_flow || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(stats?.portfolio?.monthly_cash_flow || 0)}
                </p>
                <p className="text-xs text-slate-500 mt-1">{formatCurrency(stats?.portfolio?.annual_cash_flow || 0)}/year</p>
              </div>

              {/* Average Returns */}
              <div className="glass-card rounded-xl p-5 border border-slate-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 rounded-lg bg-amber-500/10">
                    <Percent className="h-5 w-5 text-amber-500" />
                  </div>
                </div>
                <p className="text-sm text-slate-400 mb-1">Avg Returns</p>
                <div className="flex items-baseline gap-3">
                  <div>
                    <p className="text-2xl font-bold text-white">{stats?.portfolio?.avg_coc || 0}%</p>
                    <p className="text-xs text-slate-500">CoC</p>
                  </div>
                  <div className="text-slate-600">|</div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats?.portfolio?.avg_cap_rate || 0}%</p>
                    <p className="text-xs text-slate-500">Cap Rate</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Deals Analytics Row */}
            {stats?.deals?.total_deals > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Deals by Grade */}
                <div className="glass-card rounded-xl p-5 border border-slate-800 lg:col-span-2">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-slate-300">Deal Quality Distribution</h3>
                    <span className="text-xs text-slate-500">{stats?.deals?.total_deals} deals analyzed</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {['A', 'B', 'C', 'D', 'F'].map((grade) => {
                      const count = stats?.deals?.deals_by_grade?.[grade] || 0;
                      const total = stats?.deals?.total_deals || 1;
                      const percentage = Math.round((count / total) * 100);
                      const colors = {
                        'A': 'bg-emerald-500',
                        'B': 'bg-cyan-500',
                        'C': 'bg-amber-500',
                        'D': 'bg-orange-500',
                        'F': 'bg-red-500'
                      };
                      return (
                        <div key={grade} className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-bold ${colors[grade].replace('bg-', 'text-')}`}>
                              {grade}
                            </span>
                            <span className="text-xs text-slate-500">{count}</span>
                          </div>
                          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${colors[grade]} rounded-full transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Total value analyzed: {formatCurrency(stats?.deals?.total_value_analyzed || 0)}
                  </p>
                </div>

                {/* Best Deal */}
                {stats?.deals?.best_deal && (
                  <div className="glass-card rounded-xl p-5 border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent">
                    <div className="flex items-center gap-2 mb-3">
                      <Award className="h-5 w-5 text-amber-500" />
                      <h3 className="text-sm font-medium text-amber-400">Top Performer</h3>
                    </div>
                    <p className="text-white font-semibold mb-1 truncate">{stats?.deals?.best_deal?.name}</p>
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-2xl font-bold text-emerald-400">{stats?.deals?.best_deal?.cash_on_cash}%</p>
                        <p className="text-xs text-slate-500">Cash on Cash</p>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs font-bold ${stats?.deals?.best_deal?.grade === 'A' ? 'bg-emerald-500/20 text-emerald-400' :
                          stats?.deals?.best_deal?.grade === 'B' ? 'bg-cyan-500/20 text-cyan-400' :
                            'bg-amber-500/20 text-amber-400'
                        }`}>
                        Grade {stats?.deals?.best_deal?.grade}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      className="w-full mt-3 text-amber-500 hover:text-amber-400 text-xs"
                      onClick={() => navigate(`/deals/${stats?.deals?.best_deal?.id}`)}
                    >
                      View Deal Details
                      <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="glass-card rounded-lg p-3 border border-slate-800 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-500/10">
              <FileText className="h-4 w-4 text-violet-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{stats?.reports_count || 0}</p>
              <p className="text-xs text-slate-500">Reports</p>
            </div>
          </div>
          <div className="glass-card rounded-lg p-3 border border-slate-800 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Users className="h-4 w-4 text-amber-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{stats?.partnerships_count || 0}</p>
              <p className="text-xs text-slate-500">Partnerships</p>
            </div>
          </div>
          <div className="glass-card rounded-lg p-3 border border-slate-800 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/10">
              <Target className="h-4 w-4 text-cyan-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{stats?.deals?.total_deals || 0}</p>
              <p className="text-xs text-slate-500">Deals</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="metric-card" data-testid="total-analyses-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <FileText className="h-5 w-5 text-amber-500" />
              </div>
              <span className="text-sm text-slate-400">Total Analyses</span>
            </div>
            <div className="text-3xl font-bold text-white font-mono">{stats?.total_analyses || 0}</div>
          </div>
          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <Home className="h-5 w-5 text-cyan-500" />
              </div>
              <span className="text-sm text-slate-400">Rental</span>
            </div>
            <div className="text-3xl font-bold text-white font-mono">{stats?.rental_count || 0}</div>
          </div>
          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-pink-500/10">
                <RefreshCw className="h-5 w-5 text-pink-500" />
              </div>
              <span className="text-sm text-slate-400">BRRRR</span>
            </div>
            <div className="text-3xl font-bold text-white font-mono">{stats?.brrrr_count || 0}</div>
          </div>
          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <TrendingUp className="h-5 w-5 text-emerald-500" />
              </div>
              <span className="text-sm text-slate-400">Flip</span>
            </div>
            <div className="text-3xl font-bold text-white font-mono">{stats?.flip_count || 0}</div>
          </div>
          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <BarChart3 className="h-5 w-5 text-purple-500" />
              </div>
              <span className="text-sm text-slate-400">Airbnb</span>
            </div>
            <div className="text-3xl font-bold text-white font-mono">{stats?.airbnb_count || 0}</div>
          </div>
        </div>

        {/* Analysis Tools */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white font-['Manrope'] mb-4">Analysis Tools</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {tools.map((tool, index) => {
              const accessible = isToolAccessible(tool.tier);
              const showLightLimit = accessible && normalizedUserTier === 'free' && tool.lightLimit;
              return (
                <button
                  key={index}
                  onClick={() => accessible && navigate(tool.path)}
                  disabled={!accessible}
                  className={`relative text-left p-5 rounded-xl border transition-all duration-300 ${accessible
                      ? 'glass-card hover:border-amber-500/30 cursor-pointer group'
                      : 'bg-slate-900/30 border-slate-800 cursor-not-allowed opacity-60'
                    }`}
                  data-testid={`tool-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {!accessible && (
                    <div className="absolute top-3 right-3">
                      <Crown className="h-4 w-4 text-amber-500" />
                    </div>
                  )}
                  <div className={`inline-flex p-3 rounded-lg ${tool.bgColor} mb-3`}>
                    <tool.icon className={`h-6 w-6 ${tool.color}`} />
                  </div>
                  <h3 className="font-semibold text-white mb-1">{tool.name}</h3>
                  <p className="text-sm text-slate-400">{tool.desc}</p>
                  {showLightLimit && (
                    <span className="inline-block mt-2 text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                      {tool.lightLimit}
                    </span>
                  )}
                  {accessible && (
                    <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-600 group-hover:text-amber-500 transition-colors" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Recent Analyses */}
        {stats?.recent_analyses?.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white font-['Manrope']">Recent Analyses</h2>
              <Button
                variant="ghost"
                className="text-amber-500 hover:text-amber-400"
                onClick={() => navigate("/rental-analysis")}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Analysis
              </Button>
            </div>
            <div className="glass-card rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase tracking-wider font-semibold">Property</th>
                    <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase tracking-wider font-semibold hidden sm:table-cell">Date</th>
                    <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase tracking-wider font-semibold">Cash Flow</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_analyses.map((analysis, index) => {
                    const type = analysis.type || 'rental';
                    const meta = analysisTypeMeta[type] || { label: type || 'Rental', icon: FileText, pathPrefix: '/rental-analysis' };
                    return (
                      <tr
                        key={index}
                        className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors cursor-pointer"
                        onClick={() => {
                          if (meta && meta.pathPrefix) {
                            navigate(`${meta.pathPrefix}/${analysis.id}`);
                          } else {
                            navigate(`/rental-analysis/${analysis.id}`);
                          }
                        }}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="text-xs px-2 py-1 rounded bg-slate-800/40 text-slate-300">{meta.label}</div>
                            <span className="text-white font-medium">{analysis.property_name}</span>
                          </div>
                        </td>
                      <td className="py-3 px-4 text-slate-400 text-sm hidden sm:table-cell">
                        {new Date(analysis.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-mono ${analysis.results?.annual_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'
                          }`}>
                          {formatCurrency(analysis.results?.annual_cash_flow || 0)}/yr
                        </span>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {stats?.total_analyses === 0 && (
          <div className="text-center py-16">
            <div className="inline-flex p-4 rounded-full bg-slate-800 mb-4">
              <Building2 className="h-8 w-8 text-slate-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No analyses yet</h3>
            <p className="text-slate-400 mb-6">Start by creating your first property analysis</p>
            <Button
              onClick={() => navigate("/rental-analysis")}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              data-testid="create-first-analysis-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Analysis
            </Button>
          </div>
        )}
      </main>

      {/* Feedback Modal */}
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
};

export default Dashboard;
